import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useTickets } from 'contexts/TicketsContext';
import type { NotificationActionsE } from 'declarations/enums/notifications';
import { WIP_NOTIFICATION_ACTION_TYPES } from 'declarations/enums/notifications';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { RoutePathsE } from 'declarations/enums/routes';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { NotificationHistoryI } from 'declarations/interfaces/notifications';
import type { NoopT } from 'declarations/types/common';
import {
  useFetchNotificationsHistory,
  useUpdateNotificationHistoryStatus,
  useUpdateNotificationsHistoryStatus,
  useUpdateNotificationsRoofTopHistoryStatus,
} from 'hooks/api/useNotifications';
import { setSelectedWipRoofTopId, setWipSearchTerm } from 'reduxStore/features/wip/wipSlice';
import { useAppDispatch } from 'reduxStore/hooks';

type HandleNotificationContentClickParamsT = {
  ticketId?: string;
  notificationId: string;
  roNumber?: string;
  /**
   * Used to disambiguate WIP-originated notifications (WARR-10276) so the click can deep-link
   * to the WIP tab instead of the ticket details route.
   */
  actionTypeId?: NotificationActionsE;
  /**
   * Rooftop scope for the notification. Used by WIP-originated notifications to pre-select
   * the dealer in the WIP dealer card on deep-link.
   */
  roofTopId?: string | null;
};

type UseNotificationsHistoryT = {
  notificationsHistory: NotificationHistoryI[] | null;
  isLoadingNotificationsHistory: boolean;
  hasUnreadNotifications: boolean;
  unreadNotificationsAmount: number;
  isPopupOpened: boolean;
  showUnreadNotifications: boolean;
  handleNotificationContentClick: (params: HandleNotificationContentClickParamsT) => () => void;
  markNotificationAsRead: (ticketId: string) => () => void;
  markNotificationsAsRead: NoopT;
  handleOpenPopup: NoopT;
  handleClosePopup: NoopT;
  toggleUnreadFilter: NoopT;
};

const useNotificationsHistory = (): UseNotificationsHistoryT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const { setTicketId, setOpenedTicketTab } = useTickets();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isPopupOpened, setPopupVisibility] = useState(false);
  const [showUnreadNotifications, setShowUnreadNotifications] = useState(false);
  const [searchParams] = useSearchParams();
  const roofTopId = searchParams.get(TicketsRefinementKeysE.RoofTopId) || '';

  /**
   * Api hooks
   */
  const notificationsHistoryResult = useFetchNotificationsHistory({
    OnlyUnread: showUnreadNotifications,
    roofTopId: roofTopId,
  });
  const updateNotificationHistoryStatusMutation = useUpdateNotificationHistoryStatus({
    onSuccess: (_, updatedNotificationId) => {
      const updatedNotifications = notificationsHistoryResult.data?.map(notification => {
        if (notification.id === updatedNotificationId && !notification.isRead) {
          return { ...notification, isRead: true };
        }

        return notification;
      });

      queryClient.setQueryData<NotificationHistoryI[]>(
        [
          RequestQueryKeysE.FetchNotificationsHistory,
          {
            OnlyUnread: showUnreadNotifications,
            roofTopId: roofTopId,
          },
        ],
        updatedNotifications
      );
    },
  });
  const updateNotificationsHistoryStatusMutation = useUpdateNotificationsHistoryStatus({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchNotificationsHistory]);
    },
  });
  const updateNotificationsRoofTopHistoryStatus = useUpdateNotificationsRoofTopHistoryStatus({
    onSuccess: (_, roofTopId) => {
      const updatedNotifications = notificationsHistoryResult.data?.map(notification => {
        return { ...notification, isRead: true };
      });

      queryClient.setQueryData<NotificationHistoryI[]>(
        [
          RequestQueryKeysE.FetchNotificationsHistory,
          {
            OnlyUnread: showUnreadNotifications,
            roofTopId: roofTopId,
          },
        ],
        updatedNotifications
      );
    },
  });

  /**
   * Variables
   */
  const unreadNotificationsAmount = useMemo(
    () => notificationsHistoryResult.data?.filter(({ isRead }) => !isRead).length || 0,
    [notificationsHistoryResult.data]
  );
  const hasUnreadNotifications = useMemo(
    () => !!notificationsHistoryResult.data?.some(({ isRead }) => !isRead),
    [notificationsHistoryResult.data]
  );

  /**
   * Handlers
   */
  const handleNotificationContentClick = useCallback(
    ({
        ticketId,
        notificationId,
        roNumber,
        actionTypeId,
        roofTopId: notificationRoofTopId,
      }: HandleNotificationContentClickParamsT) =>
      () => {
        updateNotificationHistoryStatusMutation.mutate(notificationId);

        const isWipNotification =
          actionTypeId != null && WIP_NOTIFICATION_ACTION_TYPES.includes(actionTypeId);

        if (isWipNotification) {
          // Deep-link straight to the WIP tab and seed the dealer + RO search so the user lands
          // on the originating record (WARR-10276 "Click navigates directly to WIP item").
          if (notificationRoofTopId) {
            dispatch(setSelectedWipRoofTopId(notificationRoofTopId));
          }
          if (roNumber) {
            dispatch(setWipSearchTerm(roNumber));
          }
          navigate(RoutePathsE.WIP);
          return;
        }

        if (ticketId) {
          const tabLabel = roNumber || ticketId;
          setOpenedTicketTab(tabLabel, ticketId);
          setTicketId(ticketId);
          navigate(`${RoutePathsE.TICKET_DETAIL}/${ticketId}`);
        }
      },
    [dispatch, navigate, setOpenedTicketTab, setTicketId, updateNotificationHistoryStatusMutation]
  );
  const markNotificationAsRead = useCallback(
    (notificationId: string) => () => {
      updateNotificationHistoryStatusMutation.mutate(notificationId);
    },
    [updateNotificationHistoryStatusMutation]
  );
  const markNotificationsAsRead = useCallback(() => {
    if (roofTopId) {
      updateNotificationsRoofTopHistoryStatus.mutate(roofTopId);
      return;
    }
    updateNotificationsHistoryStatusMutation.mutate();
  }, [
    updateNotificationsRoofTopHistoryStatus,
    roofTopId,
    updateNotificationsHistoryStatusMutation,
  ]);
  const handleOpenPopup = useCallback(() => {
    setPopupVisibility(true);
  }, []);
  const handleClosePopup = useCallback(() => {
    setPopupVisibility(false);
  }, []);
  const toggleUnreadFilter = useCallback(
    () => setShowUnreadNotifications(!showUnreadNotifications),
    [showUnreadNotifications]
  );

  return {
    notificationsHistory: notificationsHistoryResult.data ?? null,
    isLoadingNotificationsHistory:
      notificationsHistoryResult.isLoading || notificationsHistoryResult.isFetching,
    hasUnreadNotifications,
    unreadNotificationsAmount,
    isPopupOpened,
    showUnreadNotifications,
    handleNotificationContentClick,
    markNotificationAsRead,
    markNotificationsAsRead,
    handleOpenPopup,
    handleClosePopup,
    toggleUnreadFilter,
  };
};

export default useNotificationsHistory;
